import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase, SUPABASE_URL, SUPABASE_ANON_KEY } from '../../lib/supabase';
import { ArrowLeft, Shield, User, Phone, Mail, Lock, IdCard, Loader2, CheckCircle2, AlertCircle, Users } from 'lucide-react';
interface AdminForm {
  full_name: string;
  email: string;
  temporary_password: string;
  phone: string;
  national_id: string;
  role: string;
  is_active: boolean;
}
interface AdminResult {
  user_id?: string;
  email?: string;
  full_name?: string;
  role?: string;
  is_active?: boolean;
}
const ROLES = [{
  value: 'super_admin',
  label: 'Super Admin',
  desc: 'Acceso total'
}, {
  value: 'platform_admin',
  label: 'Admin de Plataforma',
  desc: 'Administración general'
}, {
  value: 'support',
  label: 'Soporte',
  desc: 'Acceso operativo'
}];
const inputClass = 'w-full bg-[#141414] border border-white/10 rounded-xl pl-12 pr-4 py-4 text-base text-white placeholder:text-white/15 focus:outline-none focus:border-[#006847] focus:ring-1 focus:ring-[#006847]';
const labelClass = 'block text-xs font-bold text-white/40 uppercase tracking-widest mb-2';
const iconClass = 'absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/20';
export function EnrollmentPlatformAdminPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState<AdminForm>({
    full_name: '',
    email: '',
    temporary_password: '',
    phone: '',
    national_id: '',
    role: 'platform_admin',
    is_active: true
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<AdminResult | null>(null);
  const updateField = (field: keyof AdminForm, value: string | boolean) => {
    setForm(prev => ({
      ...prev,
      [field]: value
    }));
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setResult(null);
    if (!form.full_name.trim()) return setError('El nombre es obligatorio.');
    if (!form.email.trim()) return setError('El correo es obligatorio.');
    if (!form.temporary_password) return setError('La contraseña es obligatoria.');
    if (form.temporary_password.length < 8) return setError('La contraseña debe tener al menos 8 caracteres.');
    if (!form.role) return setError('El rol es obligatorio.');
    setIsSubmitting(true);
    const payload = {
      fullName: form.full_name.trim(),
      email: form.email.trim().toLowerCase(),
      password: form.temporary_password,
      phone: form.phone.trim() || null,
      national_id: form.national_id.trim() || null,
      role: form.role,
      isActive: form.is_active
    };
    console.log('[EnrollmentPlatformAdmin] payload keys:', Object.keys(payload));
    // Get user session for Authorization
    const {
      data: sessionData
    } = await supabase.auth.getSession();
    const accessToken = sessionData?.session?.access_token;
    console.log('[EnrollmentPlatformAdmin] Session before submit:', {
      hasSession: !!sessionData?.session,
      accessToken: accessToken ? 'present' : 'MISSING',
      userId: sessionData?.session?.user?.id ?? null
    });
    if (!accessToken) {
      setError('No hay sesión activa. Inicia sesión primero en /admin/login');
      setIsSubmitting(false);
      return;
    }
    let data: any = null;
    try {
      const res = await fetch(`${SUPABASE_URL}/functions/v1/create-platform-admin`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
          apikey: SUPABASE_ANON_KEY
        },
        body: JSON.stringify(payload)
      });
      data = await res.json();
      console.log('response:', {
        status: res.status,
        data
      });
      if (!res.ok) {
        console.error('backend error:', data);
        const raw = (data?.error || data?.details || data?.message || '').toLowerCase();
        let friendlyMsg = data?.error || data?.details || data?.message || `Error del servidor (${res.status})`;
        if (raw.includes('email') || raw.includes('correo') || raw.includes('already registered') || raw.includes('already been registered')) {
          friendlyMsg = `Ya existe un usuario con el correo "${form.email}".`;
        } else if (raw.includes('national_id') || raw.includes('cédula')) {
          friendlyMsg = `Ya existe un perfil con la cédula "${form.national_id}".`;
        }
        setError(friendlyMsg);
        setIsSubmitting(false);
        return;
      }
    } catch (fetchErr: any) {
      console.error('fetch error:', fetchErr);
      setError(fetchErr.message || 'Error de conexión con el servidor.');
      setIsSubmitting(false);
      return;
    }
    setIsSubmitting(false);
    if (!data?.success) {
      setError(data?.error || data?.details || 'Error al crear el admin.');
      return;
    }
    setResult({
      user_id: data.admin_user?.user_id,
      email: data.admin_user?.email || form.email,
      full_name: data.admin_user?.full_name || form.full_name,
      role: data.admin_user?.role || form.role,
      is_active: data.admin_user?.is_active ?? form.is_active
    });
  };
  const getRoleLabel = (value: string) => ROLES.find(r => r.value === value)?.label || value;
  if (result) {
    return <div className="min-h-screen bg-[#0B0B0B] text-white font-sans flex flex-col items-center justify-center p-6" data-id="element-4011">
        <div className="max-w-md w-full space-y-6 text-center" data-id="element-4012">
          <div className="w-20 h-20 bg-[#006847]/20 rounded-full flex items-center justify-center mx-auto" data-id="element-4013">
            <CheckCircle2 className="w-10 h-10 text-[#00a870]" data-id="element-4014" />
          </div>
          <h1 className="text-2xl font-bold" data-id="element-4015">Admin de NOIZA creado</h1>
          <p className="text-white/50" data-id="element-4016">
            La cuenta de administrador fue creada exitosamente.
          </p>

          <div className="bg-[#141414] border border-white/10 rounded-2xl p-5 text-left space-y-3 text-sm" data-id="element-4017">
            <div className="flex justify-between" data-id="element-4018">
              <span className="text-white/50" data-id="element-4019">Nombre</span>
              <strong data-id="element-4020">{result.full_name}</strong>
            </div>
            <div className="flex justify-between" data-id="element-4021">
              <span className="text-white/50" data-id="element-4022">Email</span>
              <strong className="font-mono text-xs" data-id="element-4023">{result.email}</strong>
            </div>
            <div className="flex justify-between" data-id="element-4024">
              <span className="text-white/50" data-id="element-4025">Rol</span>
              <span className="inline-flex items-center gap-1.5 bg-[#006847]/20 text-[#00a870] text-xs font-bold px-2.5 py-0.5 rounded-lg" data-id="element-4026">
                <Shield className="w-3 h-3" data-id="element-4027" />
                {getRoleLabel(result.role || '')}
              </span>
            </div>
            <div className="flex justify-between" data-id="element-4028">
              <span className="text-white/50" data-id="element-4029">Estado</span>
              <strong className={result.is_active ? 'text-[#00a870]' : 'text-red-400'} data-id="element-4030">
                {result.is_active ? 'Activo' : 'Inactivo'}
              </strong>
            </div>
            <div className="flex justify-between" data-id="element-4031">
              <span className="text-white/50" data-id="element-4032">Contraseña temporal</span>
              <strong className="font-mono text-xs" data-id="element-4033">
                {form.temporary_password}
              </strong>
            </div>
            {result.user_id && <div className="flex justify-between" data-id="element-4034">
                <span className="text-white/50" data-id="element-4035">User ID</span>
                <strong className="font-mono text-[10px] text-white/60 truncate max-w-[200px]" data-id="element-4036">
                  {result.user_id}
                </strong>
              </div>}
          </div>

          <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-xl p-4 text-left" data-id="element-4037">
            <p className="text-sm text-yellow-300" data-id="element-4038">
              ⚠️ Comparte las credenciales de forma segura. La contraseña
              temporal debe cambiarse en el primer inicio de sesión.
            </p>
          </div>

          <div className="flex gap-3 pt-4" data-id="element-4039">
            <button onClick={() => {
            setResult(null);
            setForm({
              full_name: '',
              email: '',
              temporary_password: '',
              phone: '',
              national_id: '',
              role: 'platform_admin',
              is_active: true
            });
          }} className="flex-1 py-3 bg-white/5 border border-white/10 rounded-xl font-bold text-white/70 hover:bg-white/10 transition-colors" data-id="element-4040">
              Crear otro admin
            </button>
            <button onClick={() => navigate('/admin/users')} className="flex-1 py-3 bg-[#006847] rounded-xl font-bold text-white hover:bg-[#005a3d] transition-colors" data-id="element-4041">
              Ver usuarios
            </button>
          </div>
        </div>
      </div>;
  }
  return <div className="min-h-screen bg-[#0B0B0B] text-white font-sans" data-id="element-4042">
      <div className="max-w-lg mx-auto px-4 sm:px-6 py-8 space-y-6" data-id="element-4043">
        {/* Header */}
        <div className="flex items-center gap-4" data-id="element-4044">
          <button onClick={() => navigate('/admin/enrollment')} className="w-10 h-10 bg-[#141414] border border-white/10 rounded-xl flex items-center justify-center hover:bg-white/5 transition-colors" data-id="element-4045">
            <ArrowLeft className="w-5 h-5" data-id="element-4046" />
          </button>
          <div data-id="element-4047">
            <h1 className="text-2xl font-bold" data-id="element-4048">Admin de NOIZA</h1>
            <p className="text-white/40 text-sm" data-id="element-4049">
              Crea usuarios internos de NOIZA con permisos de plataforma.
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6" data-id="element-4050">
          <div className="space-y-4" data-id="element-4051">
            <div className="flex items-center gap-2 pb-1 border-b border-white/5" data-id="element-4052">
              <Shield className="w-4 h-4 text-[#00a870]" data-id="element-4053" />
              <h2 className="text-sm font-bold text-white/60 uppercase tracking-widest" data-id="element-4054">
                Datos del admin
              </h2>
            </div>

            {/* Name */}
            <div data-id="element-4055">
              <label className={labelClass} data-id="element-4056">Nombre completo</label>
              <div className="relative" data-id="element-4057">
                <User className={iconClass} data-id="element-4058" />
                <input type="text" value={form.full_name} onChange={e => updateField('full_name', e.target.value)} placeholder="Juan Pérez" className={inputClass} required data-id="element-4059" />
              </div>
            </div>

            {/* Email */}
            <div data-id="element-4060">
              <label className={labelClass} data-id="element-4061">Correo electrónico</label>
              <div className="relative" data-id="element-4062">
                <Mail className={iconClass} data-id="element-4063" />
                <input type="email" value={form.email} onChange={e => updateField('email', e.target.value)} placeholder="admin@noiza.com" className={inputClass} required data-id="element-4064" />
              </div>
            </div>

            {/* Password */}
            <div data-id="element-4065">
              <label className={labelClass} data-id="element-4066">Contraseña temporal</label>
              <div className="relative" data-id="element-4067">
                <Lock className={iconClass} data-id="element-4068" />
                <input type="text" value={form.temporary_password} onChange={e => updateField('temporary_password', e.target.value)} placeholder="Mínimo 8 caracteres" className={`${inputClass} font-mono`} required minLength={8} data-id="element-4069" />
              </div>
            </div>

            {/* Phone */}
            <div data-id="element-4070">
              <label className={labelClass} data-id="element-4071">
                Teléfono{' '}
                <span className="text-white/20 normal-case" data-id="element-4072">(opcional)</span>
              </label>
              <div className="relative" data-id="element-4073">
                <Phone className={iconClass} data-id="element-4074" />
                <input type="tel" value={form.phone} onChange={e => updateField('phone', e.target.value)} placeholder="88888888" className={inputClass} data-id="element-4075" />
              </div>
            </div>

            {/* National ID */}
            <div data-id="element-4076">
              <label className={labelClass} data-id="element-4077">
                Cédula{' '}
                <span className="text-white/20 normal-case" data-id="element-4078">(opcional)</span>
              </label>
              <div className="relative" data-id="element-4079">
                <IdCard className={iconClass} data-id="element-4080" />
                <input type="text" value={form.national_id} onChange={e => updateField('national_id', e.target.value)} placeholder="Ej: 304590658" className={inputClass} data-id="element-4081" />
              </div>
            </div>

            {/* Role */}
            <div data-id="element-4082">
              <label className={labelClass} data-id="element-4083">Rol de plataforma</label>
              <div className="relative" data-id="element-4084">
                <Shield className={iconClass} data-id="element-4085" />
                <select value={form.role} onChange={e => updateField('role', e.target.value)} className={`${inputClass} appearance-none cursor-pointer`} required data-id="element-4086">
                  {ROLES.map(r => <option key={r.value} value={r.value} className="bg-[#141414] text-white" data-id="element-4087">
                      {r.label}
                    </option>)}
                </select>
              </div>
              <div className="mt-2 bg-white/5 border border-white/5 rounded-lg p-3 space-y-1" data-id="element-4088">
                {ROLES.map(r => <div key={r.value} className="flex items-center gap-2 text-xs" data-id="element-4089">
                    <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${form.role === r.value ? 'bg-[#00a870]' : 'bg-white/20'}`} data-id="element-4090" />
                    <span className={form.role === r.value ? 'text-white font-bold' : 'text-white/40'} data-id="element-4091">
                      {r.label}:
                    </span>
                    <span className="text-white/30" data-id="element-4092">{r.desc}</span>
                  </div>)}
              </div>
            </div>

            {/* Active Toggle */}
            <div className="flex items-center justify-between bg-[#141414] border border-white/10 rounded-xl p-4" data-id="element-4093">
              <span className="text-sm font-medium text-white/70" data-id="element-4094">
                Usuario activo
              </span>
              <button type="button" onClick={() => updateField('is_active', !form.is_active)} className={`w-14 h-8 rounded-full transition-colors relative ${form.is_active ? 'bg-[#006847]' : 'bg-white/10'}`} data-id="element-4095">
                <div className={`w-6 h-6 bg-white rounded-full absolute top-1 transition-transform ${form.is_active ? 'translate-x-7' : 'translate-x-1'}`} data-id="element-4096" />
              </button>
            </div>
          </div>

          {/* Error */}
          {error && <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-xl flex items-start gap-3 text-red-400" data-id="element-4097">
              <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" data-id="element-4098" />
              <div className="text-sm whitespace-pre-line" data-id="element-4099">{error}</div>
            </div>}

          {/* Submit */}
          <button type="submit" disabled={isSubmitting} className="w-full py-5 bg-[#006847] hover:bg-[#005a3d] disabled:bg-white/5 disabled:text-white/30 text-white font-black text-lg rounded-xl transition-all flex items-center justify-center gap-2 active:scale-95" data-id="element-4100">
            {isSubmitting ? <>
                <Loader2 className="w-6 h-6 animate-spin" data-id="element-4101" />
                Creando admin...
              </> : <>
                <Shield className="w-5 h-5" data-id="element-4102" />
                Crear Admin de NOIZA
              </>}
          </button>
        </form>
      </div>
    </div>;
}